import { loader, useLoadedData } from "./loader";
import * as Accounts from "@/models/accounts";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { PageModule } from "@/routes/types";
import { usePaths } from "@/routes/paths";
import { AccountMcpConnectionsPage, showErrorToast, showSuccessToast } from "turboui";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";

export default { name: "AccountMcpConnectionsPage", loader, Page } as PageModule;

function Page() {
  const { t } = useTranslation();
  const paths = usePaths();
  const formattedTimePreferences = useFormattedTimePreferences();
  const { mcpGrants } = useLoadedData();
  const { mutateAsync: revokeGrant } = Accounts.useRevokeMcpGrant();
  const [grants, setGrants] = React.useState<Accounts.McpGrant[]>(() => sortMcpGrants(mcpGrants));
  const [pendingRevokeIds, setPendingRevokeIds] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    setGrants(sortMcpGrants(mcpGrants));
  }, [mcpGrants]);

  const handleRevokeGrant = React.useCallback(
    async (grantId: string) => {
      if (pendingRevokeIds[grantId]) return;

      const revokedGrant = grants.find((grant) => grant.id === grantId);
      if (!revokedGrant) return;

      setPendingRevokeIds((prev) => ({ ...prev, [grantId]: true }));
      setGrants((prev) => prev.filter((grant) => grant.id !== grantId));

      try {
        await revokeGrant({ id: grantId });
        showSuccessToast(t("Connection Revoked"), t("The MCP client can no longer access your account."));
      } catch {
        setGrants((prev) => restoreGrant(prev, revokedGrant));
        showErrorToast(t("Failed To Revoke Connection"), t("Please try again."));
      } finally {
        setPendingRevokeIds((prev) => {
          const next = { ...prev };
          delete next[grantId];
          return next;
        });
      }
    },
    [grants, pendingRevokeIds, revokeGrant, t],
  );

  return (
    <AccountMcpConnectionsPage
      grants={grants}
      pendingRevokeIds={pendingRevokeIds}
      onRevokeGrant={handleRevokeGrant}
      homePath={paths.homePath()}
      securityPath={paths.accountSecurityPath()}
      mcpServerUrl={`${window.location.origin}/mcp`}
      formattedTimePreferences={formattedTimePreferences}
    />
  );
}

function sortMcpGrants(grants: Accounts.McpGrant[]) {
  return [...grants].sort((a, b) => new Date(b.insertedAt).getTime() - new Date(a.insertedAt).getTime());
}

function restoreGrant(grants: Accounts.McpGrant[], revokedGrant: Accounts.McpGrant) {
  if (grants.some((grant) => grant.id === revokedGrant.id)) {
    return grants;
  }

  return sortMcpGrants([...grants, revokedGrant]);
}
